// components/GlobalSidebar.jsx
import React, { useEffect, useLayoutEffect, useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  useMediaQuery,
  useTheme,
  Divider,
  Typography,
  Tooltip,
  Collapse,
  Badge,
} from "@mui/material";
import {
  Person as PersonIcon,
  Inventory2 as LostAndFoundIcon,
  Event as EventIcon,
  Campaign as AnnouncementsIcon,
  MenuBook as ResourcesIcon,
  QuestionAnswer as DoubtsIcon,
  School as CollegeIcon,
  // Settings as SettingsIcon,
  Dashboard as DashboardIcon,
  People as ManageStudentIcon,
  Groups as ManageClubIcon,
  CalendarMonth as ManageEventsIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";
import api from "../api";

export default function GlobalSidebar({ hideManagement = false } = {}) {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const SIDEBAR_HIDDEN_STORAGE_KEY = 'peerCampus.sidebar.hidden';

  const gradientText = {
    background:
      "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  };

  const gradientId = "peerCampusSidebarGradient";
  const gradientIcon = {
    '& svg': {
      fill: `url(#${gradientId}) !important`,
    },
    '& svg path': {
      fill: `url(#${gradientId}) !important`,
    },
  };
  
  // State management
  const [hidden, setHidden] = useState(() => {
    try {
      const saved = localStorage.getItem(SIDEBAR_HIDDEN_STORAGE_KEY);
      if (saved == null) return false;
      return JSON.parse(saved) === true;
    } catch {
      return false;
    }
  });
  const [role, setRole] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    mySpace: true,
    campus: true,
    manage: true,
  });
  const [desktopMetrics, setDesktopMetrics] = useState(() => ({
    top: 70,
    height: typeof window !== 'undefined' ? Math.max(220, window.innerHeight - 70) : 700,
  }));

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_HIDDEN_STORAGE_KEY, JSON.stringify(hidden));
    } catch {
      // ignore storage failures
    }
  }, [hidden]);

  useLayoutEffect(() => {
    if (isMobile) return;

    const getNavbarBottom = () => {
      const nav =
        document.querySelector('[data-post-login-navbar="true"]') ||
        document.querySelector('.MuiAppBar-root') ||
        document.querySelector('header');

      if (!nav) return 70;
      const rect = nav.getBoundingClientRect();
      return Math.max(0, Math.round(rect.bottom));
    };

    const getFooterTop = () => {
      const footer =
        document.querySelector('#post-login-footer') ||
        document.querySelector('[data-post-login-footer="true"]') ||
        document.querySelector('footer');

      if (!footer) return window.innerHeight;
      return Math.round(footer.getBoundingClientRect().top);
    };

    const measure = () => {
      const viewportHeight = window.innerHeight;
      const top = getNavbarBottom();
      const footerTop = getFooterTop();
      const usableBottom = Math.min(viewportHeight, footerTop);
      const height = Math.max(220, usableBottom - top);

      setDesktopMetrics((prev) => {
        if (prev.top === top && prev.height === height) return prev;
        return { top, height };
      });
    };

    measure();

    const onScroll = () => measure();
    const onResize = () => measure();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => measure());
      const footer =
        document.querySelector('#post-login-footer') ||
        document.querySelector('[data-post-login-footer="true"]') ||
        document.querySelector('footer');
      const nav =
        document.querySelector('[data-post-login-navbar="true"]') ||
        document.querySelector('.MuiAppBar-root') ||
        document.querySelector('header');

      if (footer) resizeObserver.observe(footer);
      if (nav) resizeObserver.observe(nav);
    }

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [isMobile, location.pathname]);
  
  // Dynamic width based on screen size
  // Slightly narrower for better content fit (per screenshot)
  const drawerWidth = isMobile ? 240 : 260;
  const collapsedWidth = 72;

  // ✅ Fetch user role from backend
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await api.get("users/me/");
        const userRole = response.role || response.user?.role;
        if (userRole) setRole(userRole);
      } catch (err) {
        console.error("Error fetching user role:", err);
      }
    };
    fetchUser();
  }, []);

  const effectiveRole = (() => {
    if (role) return String(role).toLowerCase();
    const path = (location?.pathname || '').toLowerCase();
    if (path.startsWith('/login/clubmember') || path.startsWith('/clubmember')) return 'club_member';
    if (path.startsWith('/login/student') || path.startsWith('/student') || path.startsWith('/your')) return 'student';
    return null;
  })();

  const getSectionRoute = (section) => {
    switch (effectiveRole) {
      case 'student':
        switch (section) {
          case 'lostfound':
            return '/student/lostfound';
          case 'yourlostfound':
            return '/student/yourlostfound';
          case 'events':
            return '/student/events';
          case 'announcements':
            return '/student/announcements';
          case 'know-your-college':
            return '/student/know-your-college';
          case 'skills':
            return '/student/skills';
          case 'yourskills':
            return '/student/yourskills';
          case 'doubts':
            return '/student/doubts';
          case 'yourdoubts':
            return '/student/yourdoubts';
          case 'eventmanagement':
            return '/eventmanagement';
          default:
            return '/login/student/home';
        }
      case 'club_member':
        switch (section) {
          case 'lostfound':
            return '/clubmember/lostfound';
          case 'yourlostfound':
            return '/clubmember/yourlostfound';
          case 'events':
            return '/clubmember/events';
          case 'announcements':
            return '/announcements';
          case 'know-your-college':
            return '/clubmember/know-your-college';
          case 'skills':
            return '/clubmember/skills';
          case 'yourskills':
            return '/clubmember/yourskills';
          case 'doubts':
            return '/clubmember/doubts';
          case 'yourdoubts':
            return '/clubmember/yourdoubts';
          case 'eventmanagement':
            return '/clubmember/eventmanagement';
          default:
            return '/login/clubmember/home';
        }
      default:
        switch (section) {
          case 'lostfound':
            return '/lostfound';
          case 'yourlostfound':
            return '/yourlostfound';
          case 'events':
            return '/events';
          case 'announcements':
            return '/announcements';
          case 'know-your-college':
            return '/know-your-college';
          case 'skills':
            return '/skills';
          case 'yourskills':
            return '/yourskills';
          case 'doubts':
            return '/doubts';
          case 'yourdoubts':
            return '/yourdoubts';
          case 'eventmanagement':
            return '/eventmanagement';
          default:
            return '/login';
        }
    }
  };

  // ✅ Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // ✅ Get dashboard/profile based on role
  const getMainRoute = () => {
    switch (effectiveRole) {
      case "admin":
        return { text: "Dashboard", icon: <DashboardIcon />, to: "/admin/dashboard" };
      case "college_admin":
        return { text: "Dashboard", icon: <DashboardIcon />, to: "/admin/dashboard" };
      case "club_member":
        return { text: "Profile", icon: <PersonIcon />, to: "/login/clubmember/home" };
      case "student":
        return { text: "Profile", icon: <PersonIcon />, to: "/login/student/home" };
      default:
        return { text: "Dashboard", icon: <DashboardIcon />, to: "/login" };
    }
  };

  // ✅ All menu items organized by sections
  const menuSections = {
    top: [getMainRoute()],
    mySpace: [
      { text: "My Lost & Found", icon: <LostAndFoundIcon />, to: getSectionRoute('yourlostfound'), roles: ["student", "club_member"] },
      { text: "My Resources", icon: <ResourcesIcon />, to: getSectionRoute('yourskills'), roles: ["student", "club_member"] },
      { text: "My Doubts", icon: <DoubtsIcon />, to: getSectionRoute('yourdoubts'), roles: ["student", "club_member"] },
    ],
    campus: [
      { text: "Lost & Found", icon: <LostAndFoundIcon />, to: getSectionRoute('lostfound') },
      { text: "Events", icon: <EventIcon />, to: getSectionRoute('events') },
      { text: "Announcements", icon: <AnnouncementsIcon />, to: getSectionRoute('announcements') },
      { text: "Know Your College", icon: <CollegeIcon />, to: getSectionRoute('know-your-college') },
      { text: "Resources", icon: <ResourcesIcon />, to: getSectionRoute('skills') },
      { text: "Doubts", icon: <DoubtsIcon />, to: getSectionRoute('doubts') },
    ],
    manage: hideManagement
      ? []
      : [
          { text: "Manage Students", icon: <ManageStudentIcon />, to: "/admin/students", roles: ["admin", "college_admin"] },
          { text: "Manage Clubs", icon: <ManageClubIcon />, to: "/admin/clubs", roles: ["admin", "college_admin"] },
          { text: "Manage Events", icon: <ManageEventsIcon />, to: getSectionRoute('eventmanagement'), roles: ["admin", "college_admin", "club_member"] },
          { text: "Manage Announcements", icon: <AnnouncementsIcon />, to: "/admin/announcements", roles: ["admin", "college_admin"] },
        ],
  };

  // ✅ Filter items based on user role
  const filterItemsByRole = (items) => {
    return items.filter(item => 
      !item.roles || (effectiveRole && item.roles.includes(effectiveRole))
    );
  };

  const isItemSelected = (to) => {
    const path = String(location?.pathname || '').toLowerCase();
    const target = String(to || '').toLowerCase();

    if (!target) return false;
    if (path === target) return true;

    // Keep highlighting for common nested/details routes
    if (target.endsWith('/events') && (path.startsWith(target) || path.includes('/event/'))) return true;
    if (target.endsWith('/lostfound') && (path.startsWith(target) || path.startsWith('/matcheditems'))) return true;
    if (target.endsWith('/yourlostfound') && path.startsWith(target)) return true;
    if (target.endsWith('/announcements') && path.startsWith(target)) return true;
    if (target.endsWith('/know-your-college') && path.startsWith(target)) return true;
    if (target.endsWith('/skills') && path.startsWith(target)) return true;
    if (target.endsWith('/yourskills') && path.startsWith(target)) return true;
    if (target.endsWith('/doubts') && path.startsWith(target)) return true;
    if (target.endsWith('/yourdoubts') && path.startsWith(target)) return true;
    if (target.endsWith('/eventmanagement') && path.startsWith(target)) return true;

    return false;
  };

  // ✅ Drawer content component
  const drawerContent = (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      bgcolor: theme.palette.background.default,
    }}>
      {/* SVG defs for gradient icons (referenced by url(#...)) */}
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#0d2a44" />
            <stop offset="30%" stopColor="#016f57" />
            <stop offset="70%" stopColor="#016f57" />
            <stop offset="100%" stopColor="#016f57" />
          </linearGradient>
        </defs>
      </svg>
      {/* Header */}
      <Box sx={{ 
        p: isMobile ? 2 : 3, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, background: "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
          PeerCampus
        </Typography>
        {!isMobile && (
          <Tooltip title="Collapse sidebar">
            <IconButton
              onClick={() => setHidden(true)}
              size="small"
              disableRipple
              disableFocusRipple
              sx={{
                '&:hover': { backgroundColor: 'transparent' },
                '&.Mui-focusVisible': { backgroundColor: 'transparent' },
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Menu Sections */}
      <List sx={{ flexGrow: 1, p: isMobile ? 1.25 : 2 }}>
        {/* Top (Profile/Dashboard) */}
        {filterItemsByRole(menuSections.top).map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: isMobile ? 0.5 : 1 }}>
            <ListItemButton
              component={Link}
              to={item.to}
              onClick={() => isMobile && setMobileOpen(false)}
              selected={isItemSelected(item.to)}
              sx={{
                borderRadius: 2,
                py: isMobile ? 1.1 : 1.5,
                px: isMobile ? 1.5 : 2,
                '&.Mui-selected': {
                  backgroundColor: theme.palette.action.selected,
                  borderLeft: `4px solid ${theme.palette.primary.main}`,
                  '&:hover': {
                    backgroundColor: theme.palette.action.selected,
                  },
                },
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <ListItemIcon sx={{ 
                minWidth: isMobile ? 36 : 40,
                ...(isItemSelected(item.to)
                  ? gradientIcon
                  : { color: theme.palette.text.secondary }),
              }}>
                {item.badge ? (
                  <Badge badgeContent={item.badge} color="error">
                    {item.icon}
                  </Badge>
                ) : item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text}
                slotProps={{
                  primary: {
                    fontWeight: isItemSelected(item.to) ? 600 : 400,
                    fontSize: isMobile ? '0.95rem' : undefined,
                    ...(isItemSelected(item.to) ? gradientText : { color: theme.palette.text.primary }),
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}

        {/* My Space Section */}
        {filterItemsByRole(menuSections.mySpace).length > 0 && (
          <>
            <Divider sx={{ my: isMobile ? 2 : 3 }} />
            <ListItemButton
              onClick={() => toggleSection('mySpace')}
              sx={{ borderRadius: 2, mb: isMobile ? 0.5 : 1, py: isMobile ? 1.1 : 1.5, px: isMobile ? 1.5 : 2 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <PersonIcon />
              </ListItemIcon>
              <ListItemText
                primary="My Space"
                slotProps={{
                  primary: {
                    fontWeight: 600,
                  },
                }}
              />
              {expandedSections.mySpace ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>

            <Collapse in={expandedSections.mySpace} timeout="auto">
              <List component="div" disablePadding>
                {filterItemsByRole(menuSections.mySpace).map((item) => (
                  <ListItem key={item.text} disablePadding sx={{ mb: isMobile ? 0.5 : 1, pl: isMobile ? 3 : 4 }}>
                    <ListItemButton
                      component={Link}
                      to={item.to}
                      onClick={() => isMobile && setMobileOpen(false)}
                      selected={isItemSelected(item.to)}
                      sx={{
                        borderRadius: 2,
                        py: isMobile ? 1.1 : 1.5,
                        px: isMobile ? 1.5 : 2,
                        '&.Mui-selected': {
                          backgroundColor: theme.palette.action.selected,
                          '&:hover': {
                            backgroundColor: theme.palette.action.selected,
                          },
                        },
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: isMobile ? 36 : 40,
                          ...(isItemSelected(item.to) ? gradientIcon : { color: theme.palette.text.secondary }),
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        slotProps={{
                          primary: {
                            fontSize: isMobile ? '0.9rem' : '0.9rem',
                            ...(isItemSelected(item.to) ? gradientText : { color: theme.palette.text.primary }),
                          },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}

        {/* Campus Section */}
        {filterItemsByRole(menuSections.campus).length > 0 && (
          <>
            <Divider sx={{ my: isMobile ? 2 : 3 }} />
            <ListItemButton
              onClick={() => toggleSection('campus')}
              sx={{ borderRadius: 2, mb: isMobile ? 0.5 : 1, py: isMobile ? 1.1 : 1.5, px: isMobile ? 1.5 : 2 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <CollegeIcon />
              </ListItemIcon>
              <ListItemText
                primary="Campus"
                slotProps={{
                  primary: {
                    fontWeight: 600,
                  },
                }}
              />
              {expandedSections.campus ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>

            <Collapse in={expandedSections.campus} timeout="auto">
              <List component="div" disablePadding>
                {filterItemsByRole(menuSections.campus).map((item) => (
                  <ListItem key={item.text} disablePadding sx={{ mb: isMobile ? 0.5 : 1, pl: isMobile ? 3 : 4 }}>
                    <ListItemButton
                      component={Link}
                      to={item.to}
                      onClick={() => isMobile && setMobileOpen(false)}
                      selected={isItemSelected(item.to)}
                      sx={{
                        borderRadius: 2,
                        py: isMobile ? 1.1 : 1.5,
                        px: isMobile ? 1.5 : 2,
                        '&.Mui-selected': {
                          backgroundColor: theme.palette.action.selected,
                          '&:hover': {
                            backgroundColor: theme.palette.action.selected,
                          },
                        },
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: isMobile ? 36 : 40,
                          ...(isItemSelected(item.to) ? gradientIcon : { color: theme.palette.text.secondary }),
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        slotProps={{
                          primary: {
                            fontSize: isMobile ? '0.9rem' : '0.9rem',
                            ...(isItemSelected(item.to) ? gradientText : { color: theme.palette.text.primary }),
                          },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}

        {/* Management Section (Conditional) */}
        {filterItemsByRole(menuSections.manage).length > 0 && (
          <>
            <Divider sx={{ my: isMobile ? 2 : 3 }} />
            <ListItemButton 
              onClick={() => toggleSection('manage')}
              sx={{ borderRadius: 2, mb: isMobile ? 0.5 : 1, py: isMobile ? 1.1 : 1.5, px: isMobile ? 1.5 : 2 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <ManageStudentIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Management"
                slotProps={{
                  primary: {
                    fontWeight: 600,
                  },
                }}
              />
              {expandedSections.manage ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            
            <Collapse in={expandedSections.manage} timeout="auto">
              <List component="div" disablePadding>
                {filterItemsByRole(menuSections.manage).map((item) => (
                  <ListItem key={item.text} disablePadding sx={{ mb: isMobile ? 0.5 : 1, pl: isMobile ? 3 : 4 }}>
                    <ListItemButton
                      component={Link}
                      to={item.to}
                      onClick={() => isMobile && setMobileOpen(false)}
                      selected={isItemSelected(item.to)}
                      sx={{
                        borderRadius: 2,
                        py: isMobile ? 1.1 : 1.5,
                        px: isMobile ? 1.5 : 2,
                        '&.Mui-selected': {
                          backgroundColor: theme.palette.action.selected,
                          '&:hover': {
                            backgroundColor: theme.palette.action.selected,
                          },
                        },
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <ListItemIcon sx={{ 
                        minWidth: isMobile ? 36 : 40,
                        ...(isItemSelected(item.to)
                          ? gradientIcon
                          : { color: theme.palette.text.secondary }),
                      }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText 
                        primary={item.text}
                        slotProps={{
                          primary: {
                            fontSize: isMobile ? '0.9rem' : '0.9rem',
                            ...(isItemSelected(item.to) ? gradientText : { color: theme.palette.text.primary }),
                          },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}
      </List>

      {/* Footer - User Info */}
      <Box sx={{ 
        p: isMobile ? 1.5 : 2, 
        mt: 'auto',
        borderTop: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.action.hover,
      }}>
        <Typography variant="body2" color="text.secondary" align="center">
          {role ? `Role: ${role.replace('_', ' ').toUpperCase()}` : 'Loading...'}
        </Typography>
      </Box>
    </Box>
  );

  // Mobile drawer
  if (isMobile) {
    return (
      <>
        {/* Mobile Menu Button */}
        <IconButton
          color="primary"
          onClick={() => setMobileOpen(!mobileOpen)}
          sx={{
            position: 'fixed',
            top: 16,
            left: 16,
            zIndex: theme.zIndex.drawer + 1,
            bgcolor: 'background.paper',
            boxShadow: 2,
            '&:hover': {
              bgcolor: 'background.paper',
            },
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              top: '70px',
              bottom: 'var(--post-login-footer-height, 0px)',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </>
    );
  }

  // Desktop drawer
  return (
    <>
      <Drawer
        variant="permanent"
        sx={{
          width: hidden ? collapsedWidth : drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: hidden ? collapsedWidth : drawerWidth,
            boxSizing: 'border-box',
            borderRight: `1px solid ${theme.palette.divider}`,
            transition: theme.transitions.create(['width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: 'hidden',
            top: `${desktopMetrics.top}px`,
            bottom: 'auto',
            height: `${desktopMetrics.height}px`,
            maxHeight: `${desktopMetrics.height}px`,
            position: 'fixed',
            alignSelf: 'auto',
          },
        }}
      >
        {hidden ? (
          // Collapsed state
          <Box sx={{ pt: 2 }}>
            {/* SVG defs for gradient icons (needed in collapsed view too) */}
            <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true" focusable="false">
              <defs>
                <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#0d2a44" />
                  <stop offset="50%" stopColor="#016f57" />
                  <stop offset="100%" stopColor="#016f57" />
                </linearGradient>
              </defs>
            </svg>

            {filterItemsByRole(menuSections.top).map((item) => (
              <Tooltip key={item.text} title={item.text} placement="right">
                <IconButton
                  component={Link}
                  to={item.to}
                  sx={{
                    my: 1,
                    mx: 'auto',
                    display: 'flex',
                    ...(isItemSelected(item.to)
                      ? gradientIcon
                      : { color: theme.palette.text.secondary }),
                    bgcolor: isItemSelected(item.to) ? theme.palette.primary.light + '20' : 'transparent',
                  }}
                >
                  {item.icon}
                </IconButton>
              </Tooltip>
            ))}

            {filterItemsByRole(menuSections.mySpace).length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                {filterItemsByRole(menuSections.mySpace).map((item) => (
                  <Tooltip key={item.text} title={item.text} placement="right">
                    <IconButton
                      component={Link}
                      to={item.to}
                      sx={{
                        my: 1,
                        mx: 'auto',
                        display: 'flex',
                        ...(isItemSelected(item.to) ? gradientIcon : { color: theme.palette.text.secondary }),
                        bgcolor: isItemSelected(item.to) ? theme.palette.primary.light + '20' : 'transparent',
                      }}
                    >
                      {item.icon}
                    </IconButton>
                  </Tooltip>
                ))}
              </>
            )}

            {filterItemsByRole(menuSections.campus).length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                {filterItemsByRole(menuSections.campus).map((item) => (
                  <Tooltip key={item.text} title={item.text} placement="right">
                    <IconButton
                      component={Link}
                      to={item.to}
                      sx={{
                        my: 1,
                        mx: 'auto',
                        display: 'flex',
                        ...(isItemSelected(item.to) ? gradientIcon : { color: theme.palette.text.secondary }),
                        bgcolor: isItemSelected(item.to) ? theme.palette.primary.light + '20' : 'transparent',
                      }}
                    >
                      {item.icon}
                    </IconButton>
                  </Tooltip>
                ))}
              </>
            )}

            {filterItemsByRole(menuSections.manage).length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                {filterItemsByRole(menuSections.manage).map((item) => (
                  <Tooltip key={item.text} title={item.text} placement="right">
                    <IconButton
                      component={Link}
                      to={item.to}
                      sx={{
                        my: 1,
                        mx: 'auto',
                        display: 'flex',
                        ...(isItemSelected(item.to)
                          ? gradientIcon
                          : { color: theme.palette.text.secondary }),
                        bgcolor: isItemSelected(item.to) ? theme.palette.secondary.light + '20' : 'transparent',
                      }}
                    >
                      {item.icon}
                    </IconButton>
                  </Tooltip>
                ))}
              </>
            )}
          </Box>
        ) : (
          // Expanded state
          drawerContent
        )}
      </Drawer>
      
      {/* Toggle Button (Always visible) */}
      <IconButton
        disableRipple
        disableFocusRipple
        onClick={() => setHidden(!hidden)}
        sx={{
          position: 'fixed',
          left: hidden ? collapsedWidth - 12 : drawerWidth - 12,
          top: `${desktopMetrics.top + (desktopMetrics.height / 2)}px`,
          transform: 'translateY(-50%) !important',
          zIndex: theme.zIndex.drawer + 1,
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '0 4px 4px 0',
          boxShadow: 'none',
          '&:hover, &:active, &.Mui-focusVisible': {
            bgcolor: 'background.paper',
            boxShadow: 'none',
            transform: 'translateY(-50%) !important',
          },
          transition: theme.transitions.create(['left'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {hidden ? <ChevronRightIcon /> : <ChevronLeftIcon />}
      </IconButton>
    </>
  );
}